import { FC } from 'react';
import { format } from 'date-fns';
import { cn } from '../../utils/cn';
import { CalendarDayProps, AvailabilityStatus } from './types';

const statusStyles: Record<AvailabilityStatus, string> = {
  available: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 border-green-200 dark:border-green-800',
  limited: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 border-yellow-200 dark:border-yellow-800',
  unavailable: 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500 border-gray-200 dark:border-slate-700 cursor-not-allowed',
  past: 'bg-gray-50 dark:bg-slate-800/50 text-gray-300 dark:text-slate-600 border-gray-100 dark:border-slate-700 cursor-not-allowed',
};

export const CalendarDay: FC<CalendarDayProps> = ({
  date,
  isSelected,
  isToday,
  isCurrentMonth,
  status,
  onClick,
  disabled,
}) => {
  const dayNumber = format(date, 'd');
  const fullDate = format(date, 'EEEE, MMMM d, yyyy');

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`${fullDate}${isToday ? ', today' : ''}${status === 'available' ? ', available' : status === 'limited' ? ', limited availability' : ', unavailable'}`}
      aria-pressed={isSelected}
      aria-current={isToday ? 'date' : undefined}
      className={cn(
        'relative aspect-square w-full flex items-center justify-center rounded-lg border text-sm font-medium transition-all',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        // Current month styling
        !isCurrentMonth && 'opacity-40',
        // Status-based styling (when not selected)
        !isSelected && !disabled && statusStyles[status],
        // Disabled state
        disabled && statusStyles[status === 'past' ? 'past' : 'unavailable'],
        // Selected state (overrides status styling)
        isSelected && 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700',
        // Today ring (when not selected)
        isToday && !isSelected && 'ring-2 ring-primary-500 ring-offset-1'
      )}
    >
      <span className={cn('relative z-10')}>{dayNumber}</span>

      {/* Availability indicator dot */}
      {!isSelected && status === 'available' && isCurrentMonth && (
        <span
          className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-green-500"
          aria-hidden="true"
        />
      )}
      {!isSelected && status === 'limited' && isCurrentMonth && (
        <span
          className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-yellow-500"
          aria-hidden="true"
        />
      )}
    </button>
  );
};

CalendarDay.displayName = 'CalendarDay';
